import {
  CreateInvoice,
  CreateInvoiceCreditDto,
  CreateQuickPayInvoice,
  EntityDto,
  Invoice,
  InvoiceCredit,
  InvoicePayment,
  InvoiceService,
  PagedInvoiceRequest,
  TransactionType,
  UpdateInvoice,
} from '@services';
import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';

class InvoiceStore {
  invoice: Invoice | undefined;
  invoices: Map<string, Invoice> = new Map();
  totalCount: number = 0;
  skipCount: number = 0;
  currentPage: number = 1;
  maxResultCount: number = 15;
  loading: boolean = false;
  creditLoading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  async getAll(pagedFilterAndSortedRequest: PagedInvoiceRequest) {
    this.setLoading(true);
    try {
      const result = await InvoiceService.getAll(pagedFilterAndSortedRequest);
      runInAction(() => {
        this.invoices.clear();
        result.items.forEach((invoice: Invoice) => {
          this.invoices.set(invoice.id, invoice);
        });
        this.totalCount = result.totalCount;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async getPublicInvoice(entityDto: EntityDto<string>) {
    this.setLoading(true);
    try {
      const result = await InvoiceService.getPublicInvoice(entityDto);
      console.log('result', result);
      runInAction(() => {
        this.invoice = result;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async get(entityDto: EntityDto<string>) {
    this.setLoading(true);
    try {
      const result = await InvoiceService.get(entityDto);
      runInAction(() => {
        this.invoice = result;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async getByOrderId(entityDto: EntityDto<string>) {
    this.setLoading(true);
    try {
      const result = await InvoiceService.getByOrderId(entityDto);
      runInAction(() => {
        this.invoice = result;
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async create(invoice: CreateInvoice): Promise<Invoice | null> {
    this.setLoading(true);
    try {
      const result = await InvoiceService.create(invoice);
      this.setLoading(false);
      message.success({
        content: 'Invoice was successfully confirmed!',
      });
      return result;
    } catch (error) {
      this.setLoading(false);
      console.error(error);
      return null;
    }
  }

  async createQuickPay(input: CreateQuickPayInvoice): Promise<Invoice | null> {
    this.setLoading(true);
    try {
      const result = await InvoiceService.createQuickPay(input);
      this.setLoading(false);
      return result;
    } catch (error) {
      this.setLoading(false);
      console.error(error);
      message.error({ content: 'QuickPay sale failed. Please try again.' });
      return null;
    }
  }

  async update(invoice: UpdateInvoice): Promise<Invoice> {
    this.setLoading(true);
    try {
      const updated = { ...this.invoice, ...invoice } as Invoice;
      const result = await InvoiceService.update(invoice);
      runInAction(() => {
        this.invoice = updated;
      });
      this.setLoading(false);
      return result;
    } catch (error) {
      this.setLoading(false);
      console.error(error);
      throw error;
    }
  }

  async getCredits(entityDto: EntityDto<string>): Promise<InvoiceCredit[]> {
    //this.setLoading(true);
    try {
      const result = await InvoiceService.getCredits(entityDto);
      //return result.data.result;
      runInAction(() => {
        //this.invoice = result;
        if (this.invoice) {
          const credits = Array.isArray(result) ? result : [result];
          this.invoice.invoiceCredits = [...(this.invoice.invoiceCredits || []), ...credits];
        }
      });

      return result;
      //this.setLoading(false);
    } catch (error) {
      //this.setLoading(false);
      console.error(error);
      return [];
    }
  }

  async addCredit(creditData: CreateInvoiceCreditDto): Promise<Invoice> {
    this.setCreditLoading(true);
    try {

      const previousPaymentCount = this.invoice?.invoicePayments?.length || 0;

      const result = await InvoiceService.addCredit(creditData);

      if (result.invoicePayments && creditData.createdBy) {
        const currentPaymentCount = result.invoicePayments.length;

        if (currentPaymentCount > previousPaymentCount) {
          result.invoicePayments = result.invoicePayments.map((payment, index) => {
            if (index >= previousPaymentCount && !payment.createdBy) {
              return {
                ...payment,
                createdBy: creditData.createdBy
              };
            }
            return payment;
          });
        }
      }

      runInAction(() => {
        if (this.invoice) {
          this.invoice = result;
        }
      });

      this.setCreditLoading(false);
      return result;

    } catch (error) {
      this.setCreditLoading(false);
      console.error('Credit creation failed:', error);
      throw error;
    }
  }

  async createPayment(invoicePayment: InvoicePayment): Promise<InvoicePayment | null> {
    try {

      const result = await InvoiceService.createPayment(invoicePayment);

      const paymentWithCreatedBy = {
        ...result,
        createdBy: result.createdBy || invoicePayment.createdBy
      };

      runInAction(() => {
        if (this.invoice && this.invoice.invoicePayments) {
          this.invoice.invoicePayments = [...this.invoice.invoicePayments, paymentWithCreatedBy];
        }
      });

      message.success({
        content: 'Payment was successfully made!',
      });
      return result;
    } catch (error) {
      return null;
    }
  }

  async logBulkPayment(input: {
    invoiceIds: string[];
    method: string;
    transactionType: TransactionType;
    note?: string;
  }): Promise<number | null> {
    try {
      const settled = await InvoiceService.logBulkPayment(input);
      message.success({
        content: `Payment logged for ${settled} invoice${settled === 1 ? '' : 's'}.`,
      });
      return settled;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setCreditLoading = (value: boolean) => {
    runInAction(() => {
      this.creditLoading = value;
    });
  };

  clearInvoice() {
    runInAction(() => {
      this.invoice = undefined;
    });
  }

  clearInvoices() {
    runInAction(() => {
      this.invoices.clear();
    });
  }

  resetInvoices = () => {
    runInAction(() => {
      this.invoices.clear();
      this.totalCount = 0;
      this.skipCount = 0;
      this.currentPage = 1;
    });
  };

  setMaxResultCount = (value: number) => {
    runInAction(() => {
      this.maxResultCount = value;
    });
  };

  setSkipCount = (value: number) => {
    runInAction(() => {
      this.skipCount = value;
    });
  };

  setCurrentPage = (value: number) => {
    runInAction(() => {
      this.currentPage = value;
    });
  };
}

export default InvoiceStore;
